import { Link } from "react-router-dom";
import BuyerPublicNavbar from "@/components/BuyerPublicNavbar";
import Footer from "@/components/Footer";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white">
      <BuyerPublicNavbar />

      <section className="bg-neutral-900 text-white py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight uppercase">Privacy Policy</h1>
          <p className="mt-3 text-neutral-400 text-sm">Last updated: January 2025</p>
        </div>
      </section>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="prose prose-neutral max-w-none">

          <section className="mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-4">1. Introduction</h2>
            <p className="text-neutral-600 leading-relaxed">
              <strong>Scaffoindia Private Limited</strong> (&ldquo;SCAFFO INDIA,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) operates the website <strong>www.scaffoindia.com</strong> and is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform and services.
            </p>
            <p className="text-neutral-600 leading-relaxed mt-3">
              This Privacy Policy is designed to help you understand how we collect, use, and protect your personal information. We encourage you to read this policy carefully. By using our Platform, you consent to the practices described in this Privacy Policy.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-4">2. Information We Collect</h2>
            <p className="text-neutral-600 leading-relaxed mb-3">
              We collect the following types of information:
            </p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2">
              <li><strong>Personal Information:</strong> Name, email address, phone number, company name, GST number, and business address provided during registration or when submitting requirements.</li>
              <li><strong>Business Information:</strong> Supplier type, products or services offered, cities served, and other business details provided in your profile.</li>
              <li><strong>Requirement Data:</strong> Project details including city, start date, duration, material specifications, and buyer contact information.</li>
              <li><strong>Quotation Data:</strong> Pricing, terms, and other information submitted through the quotation system.</li>
              <li><strong>Usage Data:</strong> IP address, browser type and version, pages visited, time spent on pages, and other diagnostic data collected through cookies and analytics tools.</li>
              <li><strong>Communication Data:</strong> Messages, feedback, and correspondence you send through the Platform.</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-4">3. Use of Information</h2>
            <p className="text-neutral-600 leading-relaxed mb-3">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2">
              <li>To provide, operate, and maintain our Platform and services.</li>
              <li>To create and manage user accounts and profiles.</li>
              <li>To facilitate the exchange of requirements between Buyers and Suppliers.</li>
              <li>To send notifications, updates, and communications related to your account and requirements.</li>
              <li>To display your public profile information in the Supplier directory.</li>
              <li>To detect and prevent fraud, abuse, and prohibited or illegal activities.</li>
              <li>To comply with legal obligations and enforce our Terms and Conditions.</li>
              <li>To improve the Platform, develop new features, and analyze usage patterns.</li>
              <li>To respond to your questions, comments, or concerns.</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-4">4. Disclosure of Information</h2>
            <p className="text-neutral-600 leading-relaxed mb-3">
              We may share your information with the following parties:
            </p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2">
              <li><strong>Suppliers:</strong> When you submit a requirement, your city, project details, and contact information may be shared with assigned Suppliers to enable them to respond with quotations.</li>
              <li><strong>Service Providers:</strong> Third-party service providers who assist us in operating the Platform, including cloud hosting, analytics, and communication services.</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, government request, or to protect our rights, safety, or the rights, safety, or property of others.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</li>
            </ul>
            <p className="text-neutral-600 leading-relaxed mt-3">
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-4">5. Data Retention</h2>
            <p className="text-neutral-600 leading-relaxed mb-3">
              We retain your information for as long as your account is active or as needed to provide services. Specific retention periods:
            </p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2">
              <li><strong>Account Data:</strong> Retained for the duration of your account activity and for a reasonable period after account closure.</li>
              <li><strong>Requirement and Quotation Data:</strong> Retained for platform records, dispute resolution, and compliance purposes.</li>
              <li><strong>Security Logs:</strong> Retained for a maximum period as permitted by applicable law.</li>
              <li><strong>Communication Data:</strong> Retained for the duration needed to process your requests and for records.</li>
            </ul>
            <p className="text-neutral-600 leading-relaxed mt-3">
              You may request the deletion of your data, subject to legal retention obligations.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-4">6. Security Measures</h2>
            <p className="text-neutral-600 leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your information against unauthorized access, alteration, disclosure, or destruction. These measures include encrypted password storage, secure HTTPS connections, access controls, and regular security assessments. However, no method of transmission over the internet or electronic storage is completely secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-4">7. Cookies</h2>
            <p className="text-neutral-600 leading-relaxed mb-3">
              We use cookies and similar tracking technologies to enhance your experience on our Platform:
            </p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2">
              <li><strong>Authentication Cookies:</strong> To maintain your login session and recognize you when you return to the Platform.</li>
              <li><strong>Analytics Cookies:</strong> To understand how you navigate and use the Platform, helping us improve our services.</li>
              <li><strong>Preference Cookies:</strong> To remember your preferences and settings for future visits.</li>
            </ul>
            <p className="text-neutral-600 leading-relaxed mt-3">
              You can manage your cookie preferences through your browser settings. Disabling cookies may affect certain features of the Platform.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-4">8. Third-Party Links</h2>
            <p className="text-neutral-600 leading-relaxed">
              Our Platform may contain links to external websites or services not operated by us. These third-party sites have their own privacy policies, and we encourage you to review them. We are not responsible for the privacy practices or content of these third-party sites. Your interactions with linked websites are governed by their respective privacy policies.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-4">9. Your Rights</h2>
            <p className="text-neutral-600 leading-relaxed mb-3">
              Depending on your location, you may have the following rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2">
              <li>The right to access the personal information we hold about you.</li>
              <li>The right to request correction of inaccurate or incomplete data.</li>
              <li>The right to request deletion of your account and associated data, subject to legal retention obligations.</li>
              <li>The right to withdraw consent for the processing of your data where consent is the basis of processing.</li>
              <li>The right to lodge a complaint with a data protection authority.</li>
            </ul>
            <p className="text-neutral-600 leading-relaxed mt-3">
              To exercise any of these rights, please contact us at <strong>Scaffoindia@gmail.com</strong> or <strong>+91-89208 93912</strong>.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-4">10. Children&apos;s Privacy</h2>
            <p className="text-neutral-600 leading-relaxed">
              Our Platform is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected personal information from a minor without parental consent, we will take steps to delete that information promptly. If you believe we have collected information from a minor, please contact us immediately.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-4">11. Changes to This Policy</h2>
            <p className="text-neutral-600 leading-relaxed">
              SCAFFO INDIA reserves the right to update or modify this Privacy Policy at any time. Any changes will be effective immediately upon posting on this page with an updated &ldquo;Last updated&rdquo; date. We encourage you to review this Privacy Policy periodically. Your continued use of the Platform after any changes constitutes your acceptance of the revised Privacy Policy.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-4">12. Contact Us</h2>
            <p className="text-neutral-600 leading-relaxed mb-3">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <ul className="list-disc list-inside text-neutral-600 space-y-1">
              <li><strong>Email:</strong> Scaffoindia@gmail.com</li>
              <li><strong>Phone:</strong> +91-89208 93912</li>
              <li><strong>Address:</strong> Vipul Business Park, Sector 48, Gurugram, Haryana, India</li>
              <li><strong>Website:</strong> www.scaffoindia.com</li>
            </ul>
          </section>

        </div>

        <div className="mt-12 pt-8 border-t border-neutral-200">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-orange-600 font-semibold hover:underline"
          >
            &larr; Back to Home
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}